import type { AppConfig } from "../config";
import type { UserContextData } from "./cognito-api";

/**
 * R8, threat protection (gairm/lms#392; crmarr/templates auth-self-hosted-sign-in.md): every sign-in call carries
 * Cognito's `UserContextData`, so the pool's adaptive authentication and compromised-credential checks see the device
 * and not a blank. The pool runs threat protection in full-function mode (infrastructure/lib/auth.ts); managed login sent
 * this for us, an API sign-in has to send it itself.
 *
 * `EncodedData` comes from Cognito's advanced-security data collection script, which AWS serves per region from
 * `amazon-cognito-assets.<region>.amazoncognito.com` and documents as the way a browser app collects it ("Collecting data
 * for threat protection in applications", Amazon Cognito Developer Guide). Why it is loaded from AWS and not bundled:
 * the file carries Amazon's copyright and no license to redistribute it, and no npm package ships it. Why there is no
 * subresource integrity: the asset host answers without CORS headers, and a browser cannot check the integrity of a
 * cross-origin script it may not read. So the CSP's `script-src` names that one AWS origin and nothing else
 * (portal-stack.ts), and the script is added only when a sign-in screen is shown, never on the rest of the app.
 *
 * What it does, read before adopting it (2017 build, `JS20171115`): collects the user agent, language, platform, time
 * zone, screen and a random per-browser id kept in localStorage, signs them with the app client id, and returns the
 * base64 blob. It makes no network request of its own.
 *
 * If the script cannot be had (blocked, offline, too slow), the sign-in goes ahead without it rather than locking every
 * person out because an AWS asset host is down; the dev walk asserts that sign-in calls carry it (e2e-dev), so a CSP or
 * loading regression fails there rather than passing quietly.
 */

interface AdvancedSecurityData {
  getData: (username: string, userPoolId: string, clientId: string) => string | undefined;
}
declare global {
  interface Window {
    AmazonCognitoAdvancedSecurityData?: AdvancedSecurityData;
  }
}

/** How long a sign-in waits for the script before going ahead without it. */
export const CONTEXT_SCRIPT_TIMEOUT_MS = 4_000;

/** The pool's region and id, read from the issuer (`https://cognito-idp.<region>.amazonaws.com/<poolId>`). */
export function poolOf(config: AppConfig): { region: string; poolId: string } | undefined {
  const issuer = config.cognito?.issuer;
  if (!issuer) return undefined;
  try {
    const u = new URL(issuer);
    const region = /^cognito-idp\.([a-z0-9-]+)\.amazonaws\.com$/.exec(u.hostname)?.[1];
    const poolId = u.pathname.replace(/^\/+|\/+$/g, "");
    return region && poolId ? { region, poolId } : undefined;
  } catch {
    return undefined;
  }
}

/** The script's address for this pool's region: the one origin the CSP allows a script from besides 'self'. */
export function contextScriptUrl(region: string): string {
  return `https://amazon-cognito-assets.${region}.amazoncognito.com/amazon-cognito-advanced-security-data.min.js`;
}

let loading: Promise<AdvancedSecurityData | undefined> | undefined;

/** Adds the script once; resolves with its API, or undefined when it did not arrive in time. Starts early, so the email step can warm it. */
export function loadContextScript(config: AppConfig, timeoutMs = CONTEXT_SCRIPT_TIMEOUT_MS): Promise<AdvancedSecurityData | undefined> {
  if (typeof window === "undefined") return Promise.resolve(undefined);
  if (window.AmazonCognitoAdvancedSecurityData) return Promise.resolve(window.AmazonCognitoAdvancedSecurityData);
  const pool = poolOf(config);
  if (!pool) return Promise.resolve(undefined);
  loading ??= new Promise<AdvancedSecurityData | undefined>((resolve) => {
    const script = document.createElement("script");
    script.src = contextScriptUrl(pool.region);
    script.async = true;
    script.dataset["starsach"] = "cognito-context-data";
    const timer = setTimeout(() => resolve(undefined), timeoutMs);
    script.onload = () => {
      clearTimeout(timer);
      resolve(window.AmazonCognitoAdvancedSecurityData);
    };
    script.onerror = () => {
      clearTimeout(timer);
      // A failed load may be tried again by the next sign-in.
      loading = undefined;
      script.remove();
      resolve(undefined);
    };
    document.head.appendChild(script);
  });
  return loading;
}

/** `UserContextData` for one sign-in call for `username`, or undefined when the browser could not produce it. */
export async function userContextData(config: AppConfig, username: string): Promise<UserContextData | undefined> {
  const pool = poolOf(config);
  const clientId = config.cognito.clientId;
  if (!pool || !clientId) return undefined;
  const asd = await loadContextScript(config);
  try {
    const encoded = asd?.getData(username, pool.poolId, clientId);
    return encoded ? { EncodedData: encoded } : undefined;
  } catch {
    return undefined;
  }
}

/** For tests: forget a load in flight. */
export function resetContextScriptForTests(): void {
  loading = undefined;
}
